import React, { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { createRichTextOptions } from "../richTextOptions";
import { slugify } from "@utils/helpers";
import ArrowIcon from "@components/icons/arrowIcon";
import { AnchorLinkIcon } from "@components/icons/anchorLinkIcon";
import * as c from "./faqs.module.scss";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const CopyLinkButton = ({ question, isCopied, onCopy, showCopiedText = true, noAnchorLinks = false }) => (
  <>
    {!noAnchorLinks && (
      <div className={c.anchorContainer}>
        {/* This is nested inside a button and the dev console hates that */}
        <div
          role="button"
          tabIndex={0}
          className={classNames(c.anchorLink, { [c.copied]: isCopied })}
          onClick={(e) => {
            e.stopPropagation();
            onCopy();
          }}
          // This makes it behave like a button
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              onCopy();
            }
          }}
          aria-label={`Copy link to "${question}"`}
        >
          <AnchorLinkIcon />
        </div>
        {showCopiedText && (
          <div className={classNames(c.copiedAnchor, { [c.visible]: isCopied })} aria-live="polite" data-nosnippet>
            Link copied
          </div>
        )}
      </div>
    )}
  </>
);

const FaqItem = ({ faq, isActive, copiedState, onToggle, onCopy, noAnchorLinks }) => {
  const questionId = slugify(faq.question);
  const renderOptions = createRichTextOptions();

  return (
    <li className={c.faqItem} data-transition="slide-enter-li">
      <div
        id={questionId}
        className={classNames(c.questionButton, { [c.active]: isActive })}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onToggle();
          }
        }}
      >
        <div className={c.questionContainer}>
          {/* Desktop anchor - hidden on mobile */}
          <CopyLinkButton
            question={faq.question}
            isCopied={copiedState}
            onCopy={onCopy}
            showCopiedText={true}
            noAnchorLinks={noAnchorLinks}
          />

          <div className={c.questionTextContainer}>
            <div className={c.questionGroup}>
              <h6 className={classNames("h6", c.questionText)}>
                {faq.question}
                {/* Mobile anchor - hidden on desktop */}
                <CopyLinkButton
                  question={faq.question}
                  isCopied={copiedState}
                  onCopy={onCopy}
                  showCopiedText={false}
                  noAnchorLinks={noAnchorLinks}
                />
              </h6>
            </div>
            <span className={c.arrowWrapper}>
              <ArrowIcon className={classNames(c.arrowIcon, { [c.active]: isActive })} direction="up" />
            </span>
          </div>
        </div>
      </div>

      <div className={classNames(c.expander, { [c.active]: isActive })}>
        <div className={c.answer}>{faq.richTextAnswer ? renderRichText(faq.richTextAnswer, renderOptions) : ""}</div>
      </div>
    </li>
  );
};

const Faqs = ({ className, heading = "Frequently Asked Questions", faqs, noAnchorLinks = false }) => {
  const [activeIndices, setActiveIndices] = useState(new Set());
  const container = useRef(null);
  const [copiedStates, setCopiedStates] = useState({});
  const [, copyToClipboard] = useCopyToClipboard();

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Remove the # symbol
      if (hash) {
        const faqIndex = faqs.findIndex((faq) => slugify(faq.question) === hash);
        if (faqIndex !== -1) {
          setActiveIndices((prev) => new Set(prev).add(faqIndex));
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ behavior: "smooth" });
            }
          }, 100);
        }
      }
    };

    // Handle initial load
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [faqs]);

  const toggleQuestion = (index) => {
    setActiveIndices((prevIndices) => {
      const newIndices = new Set(prevIndices);
      const wasOpen = newIndices.has(index);

      if (wasOpen) {
        newIndices.delete(index);
        // Only refresh ScrollTrigger when closing to recalculate positions
        setTimeout(() => {
          ScrollTrigger.refresh();
        }, 300); // Wait for CSS transition to complete
      } else {
        newIndices.add(index);
      }

      return newIndices;
    });
  };

  const handleCopy = (index, question) => {
    const questionId = slugify(question);
    const url = `${window.location.origin}${window.location.pathname}#${questionId}`;

    copyToClipboard(url);
    setCopiedStates((prev) => ({ ...prev, [index]: true }));

    // Open the FAQ when copying the link
    setActiveIndices((prev) => new Set(prev).add(index));

    setTimeout(() => {
      setCopiedStates((prev) => ({ ...prev, [index]: false }));
    }, 1500);
  };

  useGSAP(
    () => {
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (faqs.length > 0) {
        gsap.set("[data-transition='slide-enter-li']", {
          y: 20,
          opacity: 0,
        });

        ScrollTrigger.batch("[data-transition='slide-enter-li']", {
          start: "top 105%",
          end: "top 100%-=25px",
          invalidateOnRefresh: true,
          refreshPriority: -1,
          onLeave: (elements) => {
            gsap.to(elements, {
              opacity: 1,
              y: 0,
              duration: reduceMotion ? 0 : 0.5,
              overwrite: true,
              stagger: {
                amount: reduceMotion ? 0 : 0.05,
              },
              onComplete: function () {
                gsap.set(this.targets(), { clearProps: "y", transition: "transform 0.3s ease-in-out" });
              },
            });
          },
          onLeaveBack: (elements) => {
            gsap.set(elements, {
              clearProps: "transition",
              y: 20,
              opacity: 0,
            });
          },
        });
      }
    },
    { scope: container, dependencies: [faqs] }
  );

  return (
    <div className={classNames(c.container, className)} ref={container}>
      {heading && (
        <h4 className={classNames("h4", c.title)} data-transition="slide-enter">
          {heading}
        </h4>
      )}

      <ul className={c.list}>
        {faqs.map((faq, index) => (
          <FaqItem
            key={index}
            noAnchorLinks={noAnchorLinks}
            faq={faq}
            index={index}
            isActive={activeIndices.has(index)}
            copiedState={copiedStates[index]}
            onToggle={() => toggleQuestion(index)}
            onCopy={() => handleCopy(index, faq.question)}
          />
        ))}
      </ul>
    </div>
  );
};

// Simplified to only handle Contentful FAQs with richTextAnswer
const getProcessedFaqs = (items) =>
  (items || []).map((faq) => ({
    ...faq,
    plainTextAnswer: faq.richTextAnswer?.raw ? documentToPlainTextString(JSON.parse(faq.richTextAnswer.raw)) : "",
  }));

export { Faqs, getProcessedFaqs };
